// Shared data for the disruption hub + storm event pages: the congested-ports leaderboard, all
// storm-exposed lanes, the port index (with coords), and storm↔port matching.

import { portStormThreat } from './ports-api'
import { KEYED_BASE } from './publicApi'
import type { Cyclone, StormExposure } from './weather-api'

const API_BASE = (import.meta.env.VITE_API_BASE as string) ?? 'https://schedulesmcp-api.fly.dev/v1'
const WEATHER_BASE = API_BASE.replace(/\/v1\/?$/, '')

export interface CongestedPort {
  port_unlocode: string
  port_name: string | null
  level: string | null
  avg_wait_days: number | null
  narrative: string | null
  observed_at: string | null
}

export interface PortIndexRow {
  code: string
  name: string
  cc: string
  region: string
  lat: number
  lng: number
  laneCount: number
  sailings: number
}

/** URL-safe storm id ('cma:3257847' → 'cma-3257847'). */
export function stormSlug(id: string): string {
  return id.replace(/:/g, '-')
}

export async function fetchCongestedPorts(): Promise<CongestedPort[]> {
  try {
    // Keyed since 07.10: through the site's key (site-api.js).
    const res = await fetch(`${KEYED_BASE}/port-congestion`)
    if (!res.ok) return []
    const json = (await res.json()) as { data?: { ports?: CongestedPort[] } }
    return json.data?.ports ?? []
  } catch {
    return []
  }
}

export async function fetchAllExposures(): Promise<StormExposure[]> {
  try {
    const res = await fetch(`${WEATHER_BASE}/weather/exposure`)
    if (!res.ok) return []
    const json = (await res.json()) as { exposures?: StormExposure[] }
    return json.exposures ?? []
  } catch {
    return []
  }
}

export async function fetchPortsIndex(): Promise<PortIndexRow[]> {
  try {
    const res = await fetch('/data/ports/index.json')
    if (!res.ok) return []
    const json = (await res.json()) as { ports?: PortIndexRow[] }
    return json.ports ?? []
  } catch {
    return []
  }
}

export interface AffectedPort {
  code: string
  name: string
  distanceKm: number
  whenIso: string | null
}

/** Ports whose position falls inside this one cyclone's forecast gale field, nearest first. */
export function portsAffectedBy(cyclone: Cyclone, ports: PortIndexRow[]): AffectedPort[] {
  const out: AffectedPort[] = []
  for (const p of ports) {
    const threat = portStormThreat({ lat: p.lat, lon: p.lng }, [cyclone])
    if (threat) {
      out.push({
        code: p.code,
        name: p.name,
        distanceKm: threat.distanceKm,
        whenIso: threat.whenIso,
      })
    }
  }
  return out.sort((a, b) => a.distanceKm - b.distanceKm)
}
